import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AdminNavbar from '../Navbars/AdminNavbar';
import AdminSidebar from '../Sidebar/AdminSidebar';
import Select from 'react-select';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
import { Landmark, Upload, RefreshCw, CheckCircle2, Clock, Copy, X, AlertTriangle } from 'lucide-react';

/**
 * Customer service: collect payment for unpaid shipments.
 *
 * Starts from the unpaid shipments, not from the customer accounts table. A
 * shipment customer service books for a walk-in carries the AGENT's account, and
 * the walk-in has no account at all — so a screen that began with "pick a cash
 * customer" listed people who owed nothing and could not find the people who did.
 * Here the dropdown is the senders of unpaid shipments, taken off the shipments
 * themselves. Nobody needs an account.
 *
 * Two ways to pay, chosen after the shipments are ticked:
 *
 *   Pay with transfer — Paystack issues a bank account on the spot. The customer
 *   transfers to it, Paystack confirms it by webhook, and the shipments settle
 *   themselves: verified, numbered, done. Nobody uploads or confirms anything.
 *
 *   Upload proof — the customer already paid into the company account. A photo of
 *   the slip goes to finance, who confirm it before anything is released.
 */

const money = (v: any) => `NGN ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const apiError = (e: any, fallback: string) =>
    e?.response?.data?.message || e?.message || fallback;

const fmtDate = (v: any) => (v ? new Date(v).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');

type SenderOption = { value: string; label: string; sender: any };

function SubmitPaymentProof() {
    // ── Who owes ────────────────────────────────────────────
    const [senders, setSenders] = useState<any[]>([]);
    const [loadingSenders, setLoadingSenders] = useState<boolean>(true);
    const [senderKey, setSenderKey] = useState<string>('');

    const [shipments, setShipments] = useState<any[]>([]);
    const [loadingShipments, setLoadingShipments] = useState<boolean>(false);
    const [selected, setSelected] = useState<number[]>([]);

    // ── How they are paying ─────────────────────────────────
    const [method, setMethod] = useState<'transfer' | 'proof' | ''>('');

    // Pay with transfer
    const [payerEmail, setPayerEmail] = useState<string>('');
    const [payerPhone, setPayerPhone] = useState<string>('');
    const [issuing, setIssuing] = useState<boolean>(false);
    const [transfer, setTransfer] = useState<any>(null);          // the open request, shown in the modal
    const [checking, setChecking] = useState<boolean>(false);
    const [secondsLeft, setSecondsLeft] = useState<number>(0);
    const [recent, setRecent] = useState<any[]>([]);
    const pollRef = useRef<any>(null);

    // Upload proof
    const [amount, setAmount] = useState<string>('');
    const [bank, setBank] = useState<string>('');
    const [teller, setTeller] = useState<string>('');
    const [paidOn, setPaidOn] = useState<string>('');
    const [note, setNote] = useState<string>('');
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

    // ── Loading ─────────────────────────────────────────────

    const loadSenders = useCallback(async () => {
        setLoadingSenders(true);
        try {
            const res = await SuperAdminApis.getUnpaidSenders();
            if (res?.success) setSenders(res.data || []);
            else toast.warn(res?.message || 'Could not load the senders');
        } catch (e) {
            toast.error(apiError(e, 'Could not load the senders'));
        } finally {
            setLoadingSenders(false);
        }
    }, []);

    const loadShipments = useCallback(async (key: string) => {
        if (!key) { setShipments([]); return; }
        setLoadingShipments(true);
        setSelected([]);
        try {
            const res = await SuperAdminApis.getUnpaidShipments(key);
            if (res?.success) setShipments(res.data || []);
            else toast.warn(res?.message || 'Could not load the shipments');
        } catch (e) {
            toast.error(apiError(e, 'Could not load the shipments'));
        } finally {
            setLoadingShipments(false);
        }
    }, []);

    const loadRecent = useCallback(async () => {
        try {
            const res = await SuperAdminApis.listTransferRequests({ per_page: 8 });
            if (res?.success) setRecent(res.data?.data || []);
        } catch {
            // The strip is a convenience; the page works without it.
        }
    }, []);

    useEffect(() => { loadSenders(); loadRecent(); }, [loadSenders, loadRecent]);

    const senderOptions: SenderOption[] = useMemo(
        () => senders.map((s: any) => ({
            value: s.key,
            sender: s,
            label: `${s.name}${s.company && s.company !== s.name ? ` (${s.company})` : ''}`
                + `${s.email ? ` — ${s.email}` : ''}${s.phone ? ` · ${s.phone}` : ''}`
                + ` · ${s.unpaid} unpaid`,
        })),
        [senders],
    );

    const sender = useMemo(() => senders.find((s: any) => s.key === senderKey) || null, [senders, senderKey]);

    const pickSender = (key: string) => {
        setSenderKey(key);
        setMethod('');
        const s = senders.find((x: any) => x.key === key);
        setPayerEmail(s?.email || '');
        setPayerPhone(s?.phone || '');
        loadShipments(key);
    };

    // ── Selection ───────────────────────────────────────────

    const selectable = useMemo(() => shipments.filter((s: any) => s.selectable), [shipments]);

    const toggle = (id: number) =>
        setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const toggleAll = () =>
        setSelected((prev) => (prev.length === selectable.length ? [] : selectable.map((s: any) => s.id)));

    const expected = useMemo(
        () => shipments.filter((s: any) => selected.includes(s.id)).reduce((sum: number, s: any) => sum + Number(s.amount || 0), 0),
        [shipments, selected],
    );

    // ── Pay with transfer ───────────────────────────────────

    /**
     * Polling while the account is open. Paystack's webhook settles the request
     * on its own; this just keeps the screen honest about it while the customer is
     * standing there — the moment it flips to paid, the modal says so.
     */
    const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

    const refreshTransfer = useCallback(async (id: any) => {
        try {
            const res = await SuperAdminApis.getTransferRequest(id);
            if (res?.success) {
                setTransfer(res.data);
                if (res.data.status !== 'pending') {
                    stopPolling();
                    loadRecent();
                    if (res.data.status === 'paid') loadShipments(senderKey);
                }
            }
        } catch {
            // Transient; the next tick tries again.
        }
    }, [senderKey, loadShipments, loadRecent]);

    useEffect(() => {
        stopPolling();
        if (transfer?.id && transfer.status === 'pending') {
            pollRef.current = setInterval(() => refreshTransfer(transfer.id), 8000);
        }
        return stopPolling;
    }, [transfer?.id, transfer?.status, refreshTransfer]);

    // Countdown, driven locally from the server's seconds_left so it does not
    // depend on the browser and server clocks agreeing.
    useEffect(() => {
        if (!transfer || transfer.status !== 'pending') return;
        setSecondsLeft(Number(transfer.seconds_left || 0));
        const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [transfer?.id, transfer?.status, transfer?.seconds_left]);

    const issueAccount = async () => {
        if (!selected.length) { toast.warn('Tick the shipments the customer is paying for'); return; }
        if (!payerEmail.trim() || !payerEmail.includes('@')) { toast.warn('Paystack needs the customer’s email to issue an account'); return; }

        setIssuing(true);
        try {
            const res = await SuperAdminApis.createTransferRequest({
                shipment_ids: selected, email: payerEmail.trim(), phone: payerPhone.trim() || undefined,
            });

            if (res?.success) {
                setTransfer(res.data);
                setSelected([]);
                loadShipments(senderKey);
                loadRecent();
            } else {
                toast.warn(res?.message || 'Could not issue an account', { autoClose: 12000 });
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not issue an account'), { autoClose: 12000 });
        } finally {
            setIssuing(false);
        }
    };

    const checkNow = async () => {
        if (!transfer) return;
        setChecking(true);
        try {
            const res = await SuperAdminApis.verifyTransferRequest(transfer.id);
            if (res?.data) setTransfer(res.data);
            if (res?.success) {
                (res.data?.status === 'paid' ? toast.success : toast.info)(res.message, { autoClose: 8000 });
                if (res.data?.status !== 'pending') { loadRecent(); loadShipments(senderKey); }
            } else {
                toast.warn(res?.message || 'Could not check with Paystack');
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not check with Paystack'));
        } finally {
            setChecking(false);
        }
    };

    /**
     * Withdraw the account so the shipments can go on a proof instead. Paystack's
     * account itself lives on until it lapses; if the customer pays into it anyway
     * the payment still settles — the money is real either way.
     */
    const cancelTransfer = async () => {
        if (!transfer) return;
        if (!window.confirm(
            `Cancel ${transfer.reference}?\n\n`
            + 'The shipments become free to pick again, so you can upload a proof for them instead. '
            + 'If the customer has already sent money to this account it will still be settled when Paystack confirms it.'
        )) return;

        setChecking(true);
        try {
            const res = await SuperAdminApis.cancelTransferRequest(transfer.id);
            if (res?.success) {
                toast.success(res.message, { autoClose: 8000 });
                stopPolling();
                setTransfer(null);
                loadRecent();
                loadShipments(senderKey);
            } else {
                toast.warn(res?.message || 'Could not cancel');
                if (res?.data) setTransfer(res.data);
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not cancel'));
        } finally {
            setChecking(false);
        }
    };

    const copy = async (text: string) => {
        try { await navigator.clipboard.writeText(text); toast.info('Copied', { autoClose: 1200 }); } catch { /* not available */ }
    };

    // ── Upload proof ────────────────────────────────────────

    const pickFile = (f: File | null) => {
        if (preview) URL.revokeObjectURL(preview);
        setFile(f);
        setPreview(f ? URL.createObjectURL(f) : null);
    };

    const submitProof = async () => {
        if (!selected.length) { toast.warn('Tick the shipments this transfer covers'); return; }
        if (!file) { toast.warn('Attach a photo of the payment proof'); return; }
        if (!amount || Number(amount) <= 0) { toast.warn('Enter the amount the customer transferred'); return; }

        const form = new FormData();
        selected.forEach((id) => form.append('shipment_ids[]', String(id)));
        form.append('amount_claimed', amount);
        form.append('image', file);
        if (bank) form.append('bank_name', bank);
        if (teller) form.append('teller_reference', teller);
        if (paidOn) form.append('paid_on', paidOn);
        if (note) form.append('note', note);

        setSubmitting(true);
        try {
            const res = await SuperAdminApis.submitPaymentProof(form);
            if (res?.success) {
                toast.success(res.message, { autoClose: 8000 });
                setSelected([]);
                setAmount(''); setBank(''); setTeller(''); setPaidOn(''); setNote('');
                pickFile(null);
                setMethod('');
                loadShipments(senderKey);
                loadSenders();
            } else {
                toast.warn(res?.message || 'Could not submit the proof', { autoClose: 12000 });
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not submit the proof'), { autoClose: 12000 });
        } finally {
            setSubmitting(false);
        }
    };

    const difference = Number(amount || 0) - expected;
    const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
    const ss = String(secondsLeft % 60).padStart(2, '0');

    return (
        <>
            <AdminSidebar title="Record Bank Transfer" />
            <div className="relative md:ml-64 bg-[#FAFAFA] min-h-screen">
                <AdminNavbar title="Record Bank Transfer" />

                <div className="lg:px-10 pb-6 px-6">
                    <ToastContainer />

                    {/* ── Who ─────────────────────────────────── */}
                    <div className="bg-white rounded-[10px] p-4 mb-4">
                        <h2 className="text-[18px] font-semibold mb-1">Collect payment for unpaid shipments</h2>
                        <p className="text-sm text-gray-500 mb-4">
                            Everyone listed here has at least one unpaid shipment. Pick the sender, tick the
                            shipments, then either issue a Paystack account for them to transfer into now, or
                            upload the proof of a transfer they already made.
                        </p>

                        <div className="flex flex-wrap items-end gap-3">
                            <div className="flex-1 min-w-[320px]">
                                <label className="block text-xs text-gray-500 mb-1">Sender with unpaid shipments</label>
                                <Select
                                    options={senderOptions}
                                    isLoading={loadingSenders}
                                    isClearable
                                    value={senderOptions.find((o) => o.value === senderKey) ?? null}
                                    onChange={(opt: any) => pickSender(opt?.value ?? '')}
                                    placeholder={loadingSenders ? 'Loading…' : 'Search by name, email, phone or company…'}
                                    noOptionsMessage={() => (senders.length ? 'No sender matches that' : 'Nothing is unpaid right now')}
                                    styles={{ control: (base) => ({ ...base, minHeight: '38px', fontSize: '14px' }) }}
                                />
                            </div>
                            <button onClick={() => { loadSenders(); if (senderKey) loadShipments(senderKey); }}
                                title="Reload"
                                className="bg-gray-100 text-gray-700 px-3 py-2 rounded-md text-sm flex items-center gap-1.5">
                                <RefreshCw size={14} /> Refresh
                            </button>
                        </div>

                        {sender && (
                            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-600 mt-3 pt-3 border-t">
                                <span><strong className="text-gray-800">{sender.name}</strong></span>
                                {sender.email && <span>{sender.email}</span>}
                                {sender.phone && <span>{sender.phone}</span>}
                                <span>{sender.unpaid} unpaid{sender.awaiting ? ` · ${sender.awaiting} awaiting finance` : ''}</span>
                            </div>
                        )}
                    </div>

                    {/* ── What ────────────────────────────────── */}
                    {senderKey && (
                        <div className="bg-white rounded-[10px] p-4 mb-4">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-medium">
                                    Unpaid shipments ({shipments.length})
                                    {selected.length > 0 && <span className="text-gray-500"> · {selected.length} selected · {money(expected)}</span>}
                                </p>
                                {selectable.length > 0 && (
                                    <button onClick={toggleAll} className="text-sm text-[#E04343] hover:underline">
                                        {selected.length === selectable.length ? 'Clear all' : 'Select all'}
                                    </button>
                                )}
                            </div>

                            {loadingShipments ? (
                                <p className="text-center text-gray-400 py-6 text-sm">Loading…</p>
                            ) : shipments.length === 0 ? (
                                <p className="text-center text-gray-500 py-6">Nothing unpaid for this sender.</p>
                            ) : (
                                <div className="overflow-x-auto max-h-96 overflow-y-auto border rounded">
                                    <table className="w-full text-sm">
                                        <thead className="text-left text-gray-500 bg-gray-50">
                                            <tr>
                                                <th className="py-2 px-2 w-8"></th>
                                                <th className="py-2 px-2">Booked</th>
                                                <th className="py-2 px-2">Sender ref</th>
                                                <th className="py-2 px-2">Receiver</th>
                                                <th className="py-2 px-2">Service</th>
                                                <th className="py-2 px-2">Status</th>
                                                <th className="py-2 px-2 text-right">Amount due</th>
                                                <th className="py-2 px-2">Payment</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {shipments.map((s: any) => (
                                                <tr key={s.id} className={`border-t ${s.selectable ? '' : 'opacity-60'}`}>
                                                    <td className="py-2 px-2">
                                                        <input type="checkbox" checked={selected.includes(s.id)}
                                                            disabled={!s.selectable}
                                                            onChange={() => toggle(s.id)}
                                                            className="w-4 h-4 accent-[#E04343]" />
                                                    </td>
                                                    <td className="py-2 px-2 whitespace-nowrap text-gray-600">{fmtDate(s.created_at)}</td>
                                                    <td className="py-2 px-2">{s.sender_ref || <span className="text-gray-400">—</span>}</td>
                                                    <td className="py-2 px-2">
                                                        {s.receiver_name || '—'}
                                                        <span className="block text-[11px] text-gray-400">
                                                            {[s.receiver_city, s.receiver_state, s.receiver_country].filter(Boolean).join(', ')}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 px-2">{s.service || '—'}</td>
                                                    <td className="py-2 px-2">{s.status || '—'}</td>
                                                    <td className="py-2 px-2 text-right font-medium">
                                                        {s.has_invoice
                                                            ? money(s.amount)
                                                            : <span className="text-amber-600 text-xs" title="No invoice — nothing to settle. Raise one on the shipment first.">no invoice</span>}
                                                    </td>
                                                    <td className="py-2 px-2 text-xs">
                                                        {s.on_proof
                                                            ? <span className="text-amber-600">awaiting finance · {s.on_proof}</span>
                                                            : s.on_transfer
                                                                ? <span className="text-blue-600">account open · {s.on_transfer}</span>
                                                                : <span className="text-gray-500">{s.payment_status || 'unpaid'}</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── How ─────────────────────────────────── */}
                    {selected.length > 0 && (
                        <div className="bg-white rounded-[10px] p-4 mb-4">
                            <h3 className="text-[16px] font-semibold mb-1">How is the customer paying?</h3>
                            <p className="text-xs text-gray-500 mb-4">
                                {selected.length} shipment(s), <strong>{money(expected)}</strong> due.
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                                <button type="button" onClick={() => setMethod('transfer')}
                                    className={`text-left border rounded-lg p-4 transition ${method === 'transfer' ? 'border-[#E04343] bg-red-50/40' : 'hover:border-gray-400'}`}>
                                    <p className="font-semibold text-sm text-gray-800 flex items-center gap-2">
                                        <Landmark size={16} className="text-[#E04343]" /> Pay with transfer now
                                        <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-green-100 text-green-700">settles itself</span>
                                    </p>
                                    <p className="text-xs text-gray-600 mt-1">
                                        Paystack issues a bank account for exactly this amount. The customer transfers to
                                        it and Paystack confirms it — the shipments are verified and numbered
                                        automatically. No proof, no finance step.
                                    </p>
                                </button>

                                <button type="button" onClick={() => setMethod('proof')}
                                    className={`text-left border rounded-lg p-4 transition ${method === 'proof' ? 'border-[#E04343] bg-red-50/40' : 'hover:border-gray-400'}`}>
                                    <p className="font-semibold text-sm text-gray-800 flex items-center gap-2">
                                        <Upload size={16} className="text-gray-700" /> Upload proof of payment
                                    </p>
                                    <p className="text-xs text-gray-600 mt-1">
                                        They already paid into the company bank account. Attach the slip; finance
                                        confirms it under Payment Proofs before anything is released.
                                    </p>
                                </button>
                            </div>

                            {method === 'transfer' && (
                                <div className="border rounded-lg p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Customer's email *</label>
                                            <input type="email" value={payerEmail} onChange={(e) => setPayerEmail(e.target.value)}
                                                className="w-full border rounded-md px-3 py-2 text-sm" placeholder="Paystack attaches the payment to this" />
                                            <p className="text-[11px] text-gray-400 mt-1">Taken from the shipment; correct it if it is wrong or blank.</p>
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Phone</label>
                                            <input value={payerPhone} onChange={(e) => setPayerPhone(e.target.value)}
                                                className="w-full border rounded-md px-3 py-2 text-sm" />
                                        </div>
                                    </div>
                                    <button onClick={issueAccount} disabled={issuing}
                                        className="bg-[#E04343] text-white px-5 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60">
                                        {issuing && <Oval height={16} width={16} color="#fff" secondaryColor="#ffffff66" strokeWidth={5} />}
                                        {issuing ? 'Asking Paystack…' : `Generate account for ${money(expected)}`}
                                    </button>
                                </div>
                            )}

                            {method === 'proof' && (
                                <div className="border rounded-lg p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Amount transferred *</label>
                                            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                                                className="w-full border rounded-md px-3 py-2 text-sm" placeholder="0.00" />
                                            <p className="text-xs mt-1">
                                                <span className="text-gray-500">Selected shipments come to {money(expected)}. </span>
                                                {amount !== '' && (
                                                    Math.abs(difference) < 0.01
                                                        ? <span className="text-green-600 font-medium">Matches.</span>
                                                        : <span className="text-[#E04343] font-medium">
                                                            {difference < 0 ? 'Short by ' : 'Over by '}{money(Math.abs(difference))}.
                                                        </span>
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Bank</label>
                                            <input value={bank} onChange={(e) => setBank(e.target.value)}
                                                className="w-full border rounded-md px-3 py-2 text-sm" placeholder="e.g. GTBank" />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Teller / transfer reference</label>
                                            <input value={teller} onChange={(e) => setTeller(e.target.value)}
                                                className="w-full border rounded-md px-3 py-2 text-sm" />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Date paid</label>
                                            <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)}
                                                className="w-full border rounded-md px-3 py-2 text-sm" />
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-xs text-gray-500 mb-1">Proof of payment *</label>
                                        <input type="file" accept="image/*,application/pdf" capture="environment"
                                            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
                                            className="block w-full text-sm border rounded-md px-3 py-2" />
                                        <p className="text-xs text-gray-400 mt-1">Photograph the teller slip or upload a screenshot. Max 8MB.</p>
                                        {preview && file?.type?.startsWith('image/') && (
                                            <img src={preview} alt="Proof preview" className="mt-2 max-h-56 rounded border object-contain bg-gray-50" />
                                        )}
                                        {file && !file.type.startsWith('image/') && (
                                            <p className="text-xs text-gray-600 mt-2">{file.name}</p>
                                        )}
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-xs text-gray-500 mb-1">Note for finance</label>
                                        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                                            className="w-full border rounded-md px-3 py-2 text-sm"
                                            placeholder="Anything finance should know — a part payment, an agreed discount…" />
                                    </div>

                                    <button onClick={submitProof} disabled={submitting}
                                        className="bg-[#E04343] text-white px-5 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60">
                                        {submitting && <Oval height={16} width={16} color="#fff" secondaryColor="#ffffff66" strokeWidth={5} />}
                                        {submitting ? 'Submitting…' : `Submit proof for ${selected.length} shipment(s)`}
                                    </button>
                                    <p className="text-xs text-gray-400 mt-2">
                                        This does not mark anything paid. Finance confirms it before the shipments are
                                        verified and their shipment numbers issued.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Recent transfer accounts ────────────── */}
                    {recent.length > 0 && (
                        <div className="bg-white rounded-[10px] p-4">
                            <p className="text-sm font-medium mb-2">Recent Paystack accounts</p>
                            <p className="text-xs text-gray-500 mb-3">
                                Reopen one if the customer is still paying, or to see what happened.
                            </p>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-left text-gray-500 bg-gray-50">
                                        <tr>
                                            <th className="py-2 px-2">Reference</th>
                                            <th className="py-2 px-2">Customer</th>
                                            <th className="py-2 px-2">Account</th>
                                            <th className="py-2 px-2 text-right">Amount</th>
                                            <th className="py-2 px-2">Status</th>
                                            <th className="py-2 px-2">Issued</th>
                                            <th className="py-2 px-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recent.map((r: any) => (
                                            <tr key={r.id} className="border-t">
                                                <td className="py-2 px-2 font-medium">{r.reference}</td>
                                                <td className="py-2 px-2">{r.customer_name || r.customer_email}</td>
                                                <td className="py-2 px-2">{r.bank_name} {r.account_number}</td>
                                                <td className="py-2 px-2 text-right">{money(r.amount)}</td>
                                                <td className="py-2 px-2">
                                                    <span className={`text-[11px] px-1.5 py-0.5 rounded ${
                                                        r.status === 'paid' ? (r.settlement?.ok ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800')
                                                            : r.status === 'pending' ? 'bg-blue-100 text-blue-700'
                                                                : 'bg-gray-200 text-gray-600'}`}>
                                                        {r.status === 'paid' && !r.settlement?.ok ? 'paid · needs finance' : r.status}
                                                    </span>
                                                </td>
                                                <td className="py-2 px-2 text-gray-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                                                <td className="py-2 px-2 text-right">
                                                    <button onClick={() => setTransfer(r)} className="text-xs text-[#E04343] hover:underline">Open</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── The account, while the customer pays ───────────── */}
            {transfer && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <div>
                                <h3 className="font-semibold">{transfer.reference}</h3>
                                <p className="text-xs text-gray-500">{transfer.customer_name || transfer.customer_email}</p>
                            </div>
                            <button onClick={() => { stopPolling(); setTransfer(null); }} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
                        </div>

                        <div className="px-5 py-4">
                            {transfer.status === 'pending' && (
                                <>
                                    <p className="text-sm text-gray-600 mb-3">
                                        Ask the customer to transfer <strong>exactly</strong> this amount to this account.
                                        It settles on its own the moment Paystack sees it. If they pay another way
                                        instead, cancel this and upload their proof.
                                    </p>
                                    <div className="border rounded-lg divide-y">
                                        {[
                                            ['Bank', transfer.bank_name],
                                            ['Account number', transfer.account_number],
                                            ['Account name', transfer.account_name],
                                            ['Amount', money(transfer.amount)],
                                        ].map(([label, value]: any) => (
                                            <div key={label} className="flex items-center justify-between px-4 py-2.5">
                                                <span className="text-xs text-gray-500">{label}</span>
                                                <span className="flex items-center gap-2">
                                                    <strong className={`text-gray-900 ${label === 'Account number' ? 'text-lg tracking-wider' : 'text-sm'}`}>{value || '—'}</strong>
                                                    {value && (
                                                        <button onClick={() => copy(String(label === 'Amount' ? Number(transfer.amount).toFixed(2) : value))}
                                                            className="text-gray-400 hover:text-gray-700" title="Copy"><Copy size={13} /></button>
                                                    )}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <p className={`text-sm mt-3 flex items-center gap-2 ${secondsLeft < 300 ? 'text-amber-700' : 'text-gray-600'}`}>
                                        <Clock size={15} />
                                        {secondsLeft > 0
                                            ? <>Account open for <strong>{mm}:{ss}</strong> · watching for the payment…</>
                                            : <>The account has expired. Check once more, then generate a new one if needed.</>}
                                    </p>
                                </>
                            )}

                            {transfer.status === 'paid' && (
                                <div className={`rounded-lg p-4 ${transfer.settlement?.ok ? 'bg-green-50' : 'bg-amber-50'}`}>
                                    <p className={`font-semibold flex items-center gap-2 ${transfer.settlement?.ok ? 'text-green-800' : 'text-amber-900'}`}>
                                        {transfer.settlement?.ok ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
                                        {transfer.settlement?.ok ? 'Paid and settled' : 'Paid — but not settled'}
                                    </p>
                                    <p className="text-sm mt-1 text-gray-700">
                                        {money(transfer.amount_paid)} received {fmtDate(transfer.paid_at)}.
                                    </p>
                                    {transfer.settlement?.ok ? (
                                        <ul className="text-sm text-gray-700 mt-2 space-y-0.5">
                                            {(transfer.shipments || []).map((s: any) => (
                                                <li key={s.id}>
                                                    Shipment #{s.id} — {s.shipment_number
                                                        ? <strong>{s.shipment_number}</strong>
                                                        : <span className="text-amber-700">number not yet issued</span>}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-amber-900 mt-2">
                                            {transfer.settlement?.reason === 'underpaid'
                                                ? `Short by ${money(transfer.settlement.shortfall)} against ${money(transfer.settlement.expected)}. Nothing was released — finance needs to look at it.`
                                                : transfer.settlement?.message || 'The settlement did not run. Finance needs to look at it.'}
                                        </p>
                                    )}
                                </div>
                            )}

                            {(transfer.status === 'expired' || transfer.status === 'failed' || transfer.status === 'cancelled') && (
                                <p className="text-sm text-gray-600 bg-gray-50 border rounded-lg p-4">
                                    {transfer.status === 'cancelled'
                                        ? 'This account was cancelled. Its shipments are free to be picked again — upload a proof, or issue a new account.'
                                        : `This account ${transfer.status} without a payment. The shipments are free to be picked again — generate a new account if the customer still intends to pay.`}
                                </p>
                            )}
                        </div>

                        <div className="flex justify-between items-center px-5 py-3 border-t">
                            <span className="text-[11px] text-gray-400">Issued {fmtDate(transfer.created_at)}{transfer.created_by ? ` by ${transfer.created_by}` : ''}</span>
                            <div className="flex gap-2">
                                {transfer.status === 'pending' && (
                                    <>
                                        <button onClick={cancelTransfer} disabled={checking}
                                            title="Withdraw this account and pay another way"
                                            className="bg-white text-[#E04343] border border-[#E04343]/40 px-4 py-2 rounded text-sm hover:bg-red-50 disabled:opacity-60">
                                            Cancel account
                                        </button>
                                        <button onClick={checkNow} disabled={checking}
                                            className="bg-gray-800 text-white px-4 py-2 rounded text-sm flex items-center gap-2 disabled:opacity-60">
                                            <RefreshCw size={14} className={checking ? 'animate-spin' : ''} /> {checking ? 'Checking…' : 'Check now'}
                                        </button>
                                    </>
                                )}
                                <button onClick={() => { stopPolling(); setTransfer(null); }}
                                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm">Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default SubmitPaymentProof;
